import { onUnmounted, watch } from 'vue';

import { useAuthStore } from '@/entities/auth/@x/messenger';
import { EchoClient } from '@/shared/websocket';

import type { MessageEvent } from './types';

interface RegisteredListener {
  eventName: string;
  callback: (event: unknown) => void;
}

const buildChannelName = (organizationId: string): string => `messenger.${organizationId}`;

/**
 * Composable для работы с приватным WebSocket-каналом мессенджера.
 *
 * Канал tenant-scoped: `messenger.{organizationId}` (id берётся из текущей
 * организации auth-store — тот же источник, что и заголовок X-Organization-Id).
 * При смене организации переподписываемся: покидаем старый канал и навешиваем
 * зарегистрированные слушатели на канал новой организации. Имена событий
 * (`message.new` и т.д.) не меняются.
 */
export const useMessengerWebSocket = () => {
  const authStore = useAuthStore();

  const listeners: RegisteredListener[] = [];
  let currentChannelName: string | null = null;

  const attachToChannel = (channelName: string): void => {
    const channel = EchoClient.private(channelName);
    for (const { eventName, callback } of listeners) {
      channel.listen(`.${eventName}`, callback);
    }
  };

  /**
   * Подписаться на события приватного канала. Дженерик по типу события: `message.*`
   * несут MessageEvent-подобную форму, `dialog.updated` — DialogUpdatedEvent.
   * @param callback - функция-обработчик события
   * @param eventName - имя события (по умолчанию 'message.new')
   */
  const subscribeToMessages = <T = MessageEvent>(
    callback: (event: T) => void,
    eventName: string = 'message.new',
  ): void => {
    const listener = callback as (event: unknown) => void;
    listeners.push({ eventName, callback: listener });

    const organizationId = authStore.currentOrganization?.id;
    if (!organizationId) {
      console.warn('[useMessengerWebSocket] Нет текущей организации — подписка на приватный канал отложена');
      return;
    }

    currentChannelName = buildChannelName(organizationId);
    EchoClient.private(currentChannelName).listen(`.${eventName}`, listener);
  };

  /**
   * Отписаться от приватного канала мессенджера и сбросить слушатели.
   */
  const unsubscribe = (): void => {
    if (currentChannelName) {
      EchoClient.leave(currentChannelName);
      currentChannelName = null;
    }
    listeners.length = 0;
  };

  // Переподписка при смене организации/локации: покидаем старый канал и
  // навешиваем все зарегистрированные слушатели на канал новой организации.
  watch(
    () => authStore.currentOrganization?.id,
    (organizationId, previousId) => {
      if (organizationId === previousId) {
        return;
      }

      if (currentChannelName) {
        EchoClient.leave(currentChannelName);
        currentChannelName = null;
      }

      if (!organizationId || listeners.length === 0) {
        return;
      }

      currentChannelName = buildChannelName(organizationId);
      attachToChannel(currentChannelName);
    },
  );

  onUnmounted(() => {
    // Соединение общее для нескольких компонентов — не рвём его здесь.
    // Отписка выполняется потребителем через unsubscribe().
  });

  return {
    subscribeToMessages,
    unsubscribe,
  };
};
